import EntityMemberCommand from "_shared/entity-members/_api/commands/entity-member-api-command";
import FeeScheduleCategoryCommand, {
  FeeScheduleCategoryCommandPayload,
} from "marketplace/teams/fee-schedules/_api/commands/fee-schedule-category-command";
import Session from "users/__session/session";
import { Masked, MaskedTeam } from "work/masked-fields";
import ReplaceableDocumentAPIRequest, {
  ReplaceableDocumentAPIRequestPayload,
} from "work/proposals/_api/commands/replaceable-document-api-request";
import WorkTeamAPICommand, { WorkTeamAPIRequestPayload } from "work/proposals/_api/commands/work-team-api-command";
import Proposal from "work/proposals/proposal";
import { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import ProposalReviewerCommand, {
  ProposalReviewerAPIRequestPayload,
} from "work/proposals/reviewers/_api/command-contracts/proposal-reviewer-command";

export type WorkProposalAPIRequestPayload = {
  /**
   * Idempotency key for drafting this proposal.
   *
   * Creation is the one proposal command that cannot name what it is writing to, because the id is
   * minted by the server. Mint this once per editor session and resend it unchanged on every retry:
   * a fresh uuid per attempt guards nothing. Every other proposal command already carries one.
   */
  commandId?: string;
  RFPId?: string;
  creatorType?: string;
  creatorEntityId?: string;
  name?: string;
  description?: string;
  clientTeam?: WorkTeamAPIRequestPayload;
  vendorTeam?: WorkTeamAPIRequestPayload;
  feeSchedule?: FeeScheduleCategoryCommandPayload[];
  startDate?: string | null;
  endDate?: string | null;
  discount?: number;
  negotiable?: boolean;
  responseDueBy?: string | null;
  supersedesId?: string;
  clientReviewers?: ProposalReviewerAPIRequestPayload[];
  vendorReviewers?: ProposalReviewerAPIRequestPayload[];
  clientPolicies?: ReplaceableDocumentAPIRequestPayload[];
  vendorPolicies?: ReplaceableDocumentAPIRequestPayload[];
  conflictsCheckWaived?: boolean | null;
  teamRestricted?: boolean | null;
  conflicts?: ReplaceableDocumentAPIRequestPayload[];
  clientTeamTemplateIds?: string[];
  vendorTeamTemplateIds?: string[];
  clientFeeScheduleTemplateIds?: string[];
  vendorFeeScheduleTemplateIds?: string[];
  redlining?: object;
  unresolvedFields?: string[];
};

export function toProposalRedlineFieldAPI(
  field: (typeof ProposalFieldName)[keyof typeof ProposalFieldName],
): string {
  switch (field) {
    case ProposalFieldName.Name:
      return "Name";
    case ProposalFieldName.Description:
      return "Description";
    case ProposalFieldName.ResponseDueBy:
      return "ResponseDueBy";
    case ProposalFieldName.StartDate:
      return "StartDate";
    case ProposalFieldName.EndDate:
      return "EndDate";
    case ProposalFieldName.Team:
    case ProposalFieldName.TeamRestriction:
      return "VendorTeam";
    case ProposalFieldName.FeeSchedule:
      return "FeeSchedule";
    case ProposalFieldName.ClientPolicies:
    case ProposalFieldName.VendorPolicies:
    case ProposalFieldName.Policies:
      return "Policies";
    case ProposalFieldName.Conflicts:
    case ProposalFieldName.WaiveConflictsCheck:
      return "Conflicts";
    case ProposalFieldName.Discount:
      return "Discount";
    default:
      throw new Error(`Unsupported unresolved proposal field '${field}' for ProposalRedlineField API.`);
  }
}

export default class WorkProposalAPIRequest {
  commandId?: string;
  RFPId?: string;
  creatorType?: string;
  creatorEntityId?: string;
  name?: string;
  description?: string;
  clientTeam?: WorkTeamAPICommand;
  vendorTeam?: WorkTeamAPICommand;
  feeSchedule?: FeeScheduleCategoryCommand[];
  startDate?: string | null;
  endDate?: string | null;
  discount?: number;
  negotiable?: boolean;
  responseDueBy?: string | null;
  supersedesId?: string;
  clientReviewers?: ProposalReviewerCommand[];
  vendorReviewers?: ProposalReviewerCommand[];
  clientPolicies?: ReplaceableDocumentAPIRequest[];
  vendorPolicies?: ReplaceableDocumentAPIRequest[];
  conflictsCheckWaived?: boolean | null;
  teamRestricted?: boolean | null;
  conflicts?: ReplaceableDocumentAPIRequest[];
  clientTeamTemplateIds?: string[];
  vendorTeamTemplateIds?: string[];
  clientFeeScheduleTemplateIds?: string[];
  vendorFeeScheduleTemplateIds?: string[];
  unresolvedFields?: string[];

  public get payload(): WorkProposalAPIRequestPayload {
    if (!this.name) {
      throw new Error("Proposal name is required.");
    }
    if (!this.description) {
      throw new Error("Proposal description is required.");
    }
    return {
      commandId: this.commandId,
      RFPId: this.RFPId,
      creatorType: this.creatorType,
      creatorEntityId: this.creatorEntityId,
      name: this.name,
      description: this.description,
      clientTeam: this.clientTeam?.payload,
      teamRestricted: this.teamRestricted,
      vendorTeam: this.vendorTeam?.payload,
      feeSchedule: this.feeSchedule?.map((category) => category.payload),
      startDate: this.startDate,
      endDate: this.endDate,
      discount: this.discount,
      negotiable: this.negotiable,
      responseDueBy: this.responseDueBy,
      supersedesId: this.supersedesId,
      clientReviewers: this.clientReviewers?.map((reviewer) => reviewer.payload),
      vendorReviewers: this.vendorReviewers?.map((reviewer) => reviewer.payload),
      clientFeeScheduleTemplateIds: this.clientFeeScheduleTemplateIds,
      vendorFeeScheduleTemplateIds: this.vendorFeeScheduleTemplateIds,
      clientTeamTemplateIds: this.clientTeamTemplateIds,
      vendorTeamTemplateIds: this.vendorTeamTemplateIds,
      clientPolicies: this.clientPolicies,
      vendorPolicies: this.vendorPolicies,
      conflictsCheckWaived: this.conflictsCheckWaived,
      conflicts: this.conflicts,
      unresolvedFields: this.unresolvedFields,
    };
  }

  constructor(
    proposal: Proposal,
    session: Readonly<Session>,
    unresolvedFields?: Set<(typeof ProposalFieldName)[keyof typeof ProposalFieldName]>,
    commandId?: string,
  ) {
    this.commandId = commandId;
    this.RFPId = proposal.RFPId?.value;
    this.name = proposal.name?.value;
    this.description = proposal.description?.value;
    this.vendorTeam =
      Masked.isPresentNotMasked(proposal.vendorTeam) && proposal.vendorTeam.leader
        ? Object.assign(new WorkTeamAPICommand(), {
            leader: proposal.vendorTeam.leader?.legalEntityId
              ? new EntityMemberCommand(proposal.vendorTeam.leader)
              : undefined,
            members: proposal.vendorTeam.members.map((member) => new EntityMemberCommand(member)),
            marketplaceId: proposal.vendorTeam.marketplaceId?.value,
          })
        : undefined;
    this.feeSchedule = Masked.isNotMasked(proposal.feeSchedule)
      ? proposal.feeSchedule.map((category) => {
          return new FeeScheduleCategoryCommand(category);
        })
      : undefined;
    this.discount = Masked.isPresentNotMasked(proposal.discount) ? proposal.discount.value : undefined;
    this.creatorType = session.accountType;
    this.creatorEntityId = session.user!.legalEntityId.value;
    this.startDate = proposal.startDate === null ? null : proposal.startDate?.toISOString();
    this.endDate = proposal.endDate === null ? null : proposal.endDate?.toISOString();
    this.responseDueBy = proposal.responseDueBy === null ? null : proposal.responseDueBy?.toISOString();
    this.negotiable = proposal.negotiable.value;
    this.clientReviewers = proposal.clientReviewers.map((reviewer) => {
      return Object.assign(new ProposalReviewerCommand(reviewer));
    });
    this.vendorReviewers = proposal.vendorReviewers.map((reviewer) => {
      return Object.assign(new ProposalReviewerCommand(reviewer));
    });
    this.supersedesId = proposal.supersedes?.id?.value;
    const clientTeam = MaskedTeam.isPresentNotMasked(proposal.clientTeam) ? proposal.clientTeam : undefined;
    this.clientTeam = Object.assign(new WorkTeamAPICommand(), {
      leader: clientTeam?.leader ? new EntityMemberCommand(clientTeam.leader) : undefined,
      members: clientTeam?.members.map((member) => new EntityMemberCommand(member)),
    });
    this.clientPolicies = Masked.isNotMasked(proposal.clientPolicies)
      ? proposal.clientPolicies.map((document) => {
          return Object.assign(new ReplaceableDocumentAPIRequest(document));
        })
      : undefined;
    this.vendorPolicies = Masked.isNotMasked(proposal.vendorPolicies)
      ? proposal.vendorPolicies.map((document) => {
          return Object.assign(new ReplaceableDocumentAPIRequest(document));
        })
      : undefined;

    this.conflictsCheckWaived = Masked.isPresentNotMasked(proposal.conflictsCheckWaived)
      ? proposal.conflictsCheckWaived.value
      : undefined;

    this.teamRestricted = Masked.isPresentNotMasked(proposal.teamRestricted)
      ? proposal.teamRestricted.value
      : undefined;

    this.conflicts = Masked.isPresentNotMasked(proposal.conflicts)
      ? proposal.conflicts.map((document) => {
          return Object.assign(new ReplaceableDocumentAPIRequest(document));
        })
      : undefined;

    this.clientTeamTemplateIds = proposal.clientTeamTemplateIds.map((id) => id.value);
    this.vendorTeamTemplateIds = proposal.vendorTeamTemplateIds.map((id) => id.value);
    this.clientFeeScheduleTemplateIds = proposal.clientFeeScheduleTemplateIds.map((id) => id.value);
    this.vendorFeeScheduleTemplateIds = proposal.vendorFeeScheduleTemplateIds.map((id) => id.value);
    this.unresolvedFields = unresolvedFields
      ? Array.from(new Set(Array.from(unresolvedFields).map(toProposalRedlineFieldAPI)))
      : undefined;
  }
}
